import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import TextInput from '../../components/TextInput';
import CheckboxInput from '../../components/CheckboxInput';
import Button from '../../components/Button';
import SkeletonLoader from '../../components/SkeletonLoader';
import leadStatusApi from '../../api/leadStatusApi';
import { useToast } from '../../contexts/ToastContext';
import { ROUTES } from '../../constants';

const EditStatus = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { addToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [generalError, setGeneralError] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const [formData, setFormData] = useState({
    name: '',
    is_default: 0,
    color: '#000000',
    is_active: 0,
  });
  const [statusFound, setStatusFound] = useState(true);

  useEffect(() => {
    const fetchStatus = async () => {
      setLoading(true);
      setGeneralError(null);
      setValidationErrors({});
      try {
        const response = await leadStatusApi.getLeadStatusByIdentifier(id);
        if (response.success && response.data) {
          const statusToEdit = response.data;
          setFormData({
            name: statusToEdit.name,
            is_default: statusToEdit.is_default ? 1 : 0,
            color: statusToEdit.color,
            is_active: statusToEdit.is_active ? 1 : 0,
          });
          setStatusFound(true);
        } else {
          setStatusFound(false);
          setGeneralError(response.message || 'Status not found.');
        }
      } catch (err) {
        console.error('Failed to fetch status:', err);
        setStatusFound(false);
        setGeneralError(err.response?.data?.message || 'Failed to load status. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchStatus();
  }, [id, addToast]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? (checked ? 1 : 0) : value,
    });
    if (validationErrors[name]) {
      setValidationErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setGeneralError(null);
    setValidationErrors({});

    try {
      await leadStatusApi.updateLeadStatus(id, formData);
      addToast('Lead status updated successfully!', 'success');
      navigate(ROUTES.LEAD_STATUS);
    } catch (err) {
      console.error('Failed to update lead status:', err);
      if (err.response && err.response.status === 422) {
        setValidationErrors(err.response.data.errors || {});
        addToast(err.response.data.message || 'Validation failed. Please check your input.', 'error');
      } else {
        addToast(err.response?.data?.message || 'Failed to update lead status. Please try again.', 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 bg-gray-800 rounded-lg shadow-md text-text-white text-center">
        <SkeletonLoader type="form" />
      </div>
    );
  }

  if (!statusFound) {
    return (
      <div className="p-6 bg-gray-800 rounded-lg shadow-md text-text-white text-center">
        <h1 className="text-3xl font-bold mb-4">Status Not Found</h1>
        <p className="text-text-gray">{generalError || 'The status you are trying to edit does not exist.'}</p>
        <Button
          onClick={() => navigate(ROUTES.LEAD_STATUS)}
          className="mt-4 bg-gray-500 hover:bg-gray-600 text-white"
        >
          Go to Status List
        </Button>
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-800 rounded-lg shadow-md text-text-white">
      <h1 className="text-3xl font-bold mb-6">Edit Status</h1>
      {generalError && <div className="text-red-500 mb-4">{generalError}</div>}
      <form onSubmit={handleSubmit}>
        <TextInput
          label="Name"
          name="name"
          id="name"
          value={formData.name}
          onChange={handleChange}
          required={true}
          placeholder="Name"
          error={validationErrors?.name?.[0]}
        />
        <TextInput
          label="Color"
          name="color"
          id="color"
          type="color"
          value={formData.color}
          onChange={handleChange}
          required={true}
          className="h-10"
          placeholder="Color"
          error={validationErrors?.color?.[0]}
        />
        <CheckboxInput
          label="Is Default"
          name="is_default"
          id="is_default"
          checked={formData.is_default}
          onChange={handleChange}
        />
        <div className="flex items-center justify-between mt-6">
          <Button
            type="submit"
            className="bg-accent-red hover:bg-red-700 text-white"
            loading={loading}
          >
            Update Status
          </Button>
          <Button
            type="button"
            onClick={() => navigate(ROUTES.LEAD_STATUS)}
            className="bg-gray-500 hover:bg-gray-600 text-white"
            disabled={loading}
          >
            Cancel
          </Button>
        </div>
      </form>
    </div>
  );
};

export default EditStatus;